import { useEffect, useMemo, useState } from "react";
import { useDebounce } from "usehooks-ts";

import { getUsers } from "../api/api";
import { getInitialsFromFullName } from "../constants/constants";
import { withNamedLabels } from "../features/billing/invoices/invoiceList.utils";
import { Period } from "../features/billing/SelectDate";
import { useAppSelector } from "../redux/redux";
import { useGetInvoiceStatusesQuery } from "../services/BillingService";
import { getLinkedFirm } from "../sessionStorage/sessionStorage";
import { useGetSearchCustomersQuery } from "../slices/ContactsSlice";
import { IUser, SelectOptionType } from "../types/types";
import { TimePeriod, useDetectTimePeriod } from "./useDetectTimePeriod";

type UseInvoiceFiltersControllerOptions = {
  defaultPeriod?: TimePeriod;
};

export const useInvoiceFiltersController = (
  options?: UseInvoiceFiltersControllerOptions,
) => {
  const user = useAppSelector((state) => state.appReducer.user);
  const defaultPeriod = options?.defaultPeriod ?? TimePeriod.YTD;

  const [searchClients, setSearchClients] = useState("");
  const [filter, setFilter] = useState<Period>({
    period: defaultPeriod,
    from: null,
    to: null,
  });
  const [filterByStatus, setFilterByStatus] = useState<SelectOptionType[]>([]);
  const [filterByUser, setFilterByUser] = useState<SelectOptionType[]>([]);
  const [filterByContact, setFilterByContact] = useState<SelectOptionType[]>(
    [],
  );
  const [filterByLabel, setFilterByLabel] = useState<SelectOptionType[]>([]);
  const [usersOptions, setUsersOptions] = useState<SelectOptionType[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);

  const orgId = getLinkedFirm()?.orgId || "";
  const debounceSearchClients = useDebounce(searchClients, 300);

  const { from, to } = useDetectTimePeriod(
    filter.period
      ? {
          period: filter.period,
          from: filter.from,
          to: filter.to,
        }
      : null,
  );

  const { data: contacts, isLoading: isLoadingContacts } =
    useGetSearchCustomersQuery(
      {
        searchQuery: debounceSearchClients || "ab",
        orgId,
        userId: user.id,
        isActive: true,
      },
      {
        skip: !orgId || !user.id,
      },
    );

  const { data: statuses } = useGetInvoiceStatusesQuery(orgId, {
    skip: !orgId,
  });

  const labelOptions = useMemo<SelectOptionType[]>(
    () =>
      // BILL-010 (QA batch 17): labels without a name aren't offered.
      withNamedLabels(statuses).map((item) => ({
        label: item?.name,
        value: item?.id,
        color: item?.color,
      })),
    [statuses],
  );

  const clientOptions = useMemo<SelectOptionType[]>(() => {
    return (
      contacts?.map((contact) => ({
        value: contact?.customerId,
        label: `${contact.customerName}`,
        avatar: getInitialsFromFullName(contact?.customerName),
        avatarColor: contact.businessContactId ? "purple" : "blue",
      })) || []
    );
  }, [contacts]);

  useEffect(() => {
    if (!orgId) return;

    setIsLoadingUsers(true);

    getUsers(orgId)
      .then((res: IUser[]) => {
        setUsersOptions(
          res?.map((item) => ({
            avatar: `${item?.userProfile?.firstName?.[0] || ""}${
              item?.userProfile?.lastName?.[0] || ""
            }`,
            label: `${item?.userProfile?.firstName || ""} ${
              item?.userProfile?.lastName || ""
            }`.trim(),
            value: item?.id,
          })) || [],
        );
      })
      .finally(() => {
        setIsLoadingUsers(false);
      });
  }, [orgId]);

  return {
    filter,
    setFilter,

    from,
    to,

    searchClients,
    setSearchClients,

    filterByStatus,
    setFilterByStatus,

    filterByUser,
    setFilterByUser,

    filterByContact,
    setFilterByContact,

    filterByLabel,
    setFilterByLabel,

    usersOptions,
    clientOptions,
    labelOptions,

    isLoadingUsers,
    isLoadingContacts,
  };
};
